import { Controller } from '@hotwired/stimulus';
import application from '~/stimulus_application';

interface Option {
  label: string;
  value: string;
}

class ComboboxFieldController extends Controller {
  static targets = ['input', 'hidden', 'dropdown', 'results', 'loading', 'empty', 'clearButton'];
  static values = {
    searchUrl: String,
    delay: { type: Number, default: 200 },
    minLength: { type: Number, default: 0 },
    autoSubmit: { type: Boolean, default: false },
    clearOnOpen: { type: Boolean, default: false },
  };

  declare readonly inputTarget: HTMLInputElement;
  declare readonly hiddenTarget: HTMLInputElement;
  declare readonly dropdownTarget: HTMLElement;
  declare readonly resultsTarget: HTMLElement;
  declare readonly loadingTarget: HTMLElement;
  declare readonly emptyTarget: HTMLElement;
  declare readonly clearButtonTarget: HTMLElement;
  declare readonly hasClearButtonTarget: boolean;
  declare readonly searchUrlValue: string;
  declare readonly delayValue: number;
  declare readonly minLengthValue: number;
  declare readonly autoSubmitValue: boolean;
  declare readonly clearOnOpenValue: boolean;

  private timeout: number | null = null;
  private boundHandleOutsideClick!: (event: MouseEvent) => void;
  private lastQuery: string | null = null;

  connect() {
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.boundHandleOutsideClick);
    this.updateClearButton();
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleOutsideClick);
    if (this.timeout) window.clearTimeout(this.timeout);
  }

  open() {
    if (!this.dropdownTarget.classList.contains('hidden')) return;
    if (this.clearOnOpenValue) {
      this.inputTarget.value = '';
      this.updateClearButton();
    }
    this.fetch(this.inputTarget.value);
  }

  input() {
    this.updateClearButton();
    if (this.timeout) window.clearTimeout(this.timeout);
    this.timeout = window.setTimeout(() => {
      this.fetch(this.inputTarget.value);
    }, this.delayValue);
  }

  clear() {
    this.inputTarget.value = '';
    this.lastQuery = '';
    this.updateClearButton();
    this.inputTarget.focus();
    this.fetch('');
  }

  keydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.close();
      this.inputTarget.blur();
    }
  }

  select(event: MouseEvent) {
    const button = event.currentTarget as HTMLElement;
    const value = button.dataset.value ?? '';
    const label = button.dataset.label ?? value;

    this.hiddenTarget.value = value;
    this.inputTarget.value = label;
    this.lastQuery = label;
    this.close();

    if (this.autoSubmitValue) {
      const form = this.element.closest('form');
      if (form) form.requestSubmit();
    }
  }

  private fetch(query: string) {
    const trimmed = query.trim();
    if (trimmed.length < this.minLengthValue && trimmed.length > 0) return;

    this.showLoading();

    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set('q', trimmed);

    window
      .fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      .then((r) => r.json())
      .then((options: Option[]) => this.renderOptions(options))
      .catch(() => this.showEmpty());
  }

  private renderOptions(options: Option[]) {
    this.loadingTarget.classList.add('hidden');

    if (options.length === 0) {
      this.resultsTarget.innerHTML = '';
      this.emptyTarget.classList.remove('hidden');
    } else {
      this.emptyTarget.classList.add('hidden');
      this.resultsTarget.innerHTML = options.map((o) => this.optionHTML(o)).join('');
      this.resultsTarget.querySelectorAll<HTMLElement>('[data-action]').forEach((el) => {
        el.addEventListener('click', this.select.bind(this));
      });
    }

    this.dropdownTarget.classList.remove('hidden');
  }

  private optionHTML(option: Option): string {
    const isCurrent = option.value === this.hiddenTarget.value;
    const activeClass = isCurrent ? 'font-semibold bg-blue-50 dark:bg-blue-900' : '';
    return `<button
      type="button"
      data-action="click"
      data-value="${this.escapeAttr(option.value)}"
      data-label="${this.escapeAttr(option.label)}"
      class="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600 ${activeClass}"
    >${this.escapeHTML(option.label)}</button>`;
  }

  private showLoading() {
    this.resultsTarget.innerHTML = '';
    this.emptyTarget.classList.add('hidden');
    this.loadingTarget.classList.remove('hidden');
    this.dropdownTarget.classList.remove('hidden');
  }

  private showEmpty() {
    this.loadingTarget.classList.add('hidden');
    this.resultsTarget.innerHTML = '';
    this.emptyTarget.classList.remove('hidden');
    this.dropdownTarget.classList.remove('hidden');
  }

  private updateClearButton() {
    if (!this.hasClearButtonTarget) return;
    const hasText = this.inputTarget.value.length > 0;
    this.clearButtonTarget.classList.toggle('hidden', !hasText);
  }

  private close() {
    this.dropdownTarget.classList.add('hidden');
    this.inputTarget.value = this.lastQuery ?? this.hiddenTarget.value;
    this.updateClearButton();
  }

  private handleOutsideClick(event: MouseEvent) {
    if (!this.element.contains(event.target as Node)) {
      this.close();
    }
  }

  private escapeHTML(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private escapeAttr(str: string): string {
    return str.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}

application.register('ui--form--combobox-field', ComboboxFieldController);
